import React, { Suspense, lazy } from 'react'
import cx from 'classnames'
import { Button, Classification, Quote } from '~elements'
import styles from './EDPFullScreenVideo.module.scss'
import { ButtonProps, ImageProps } from '~types'
import { getCustomBreakpoint } from 'utils/hooks'
import { gaBuyTicketsClick } from 'utils/hooks/gaBtnClick'
import { useRouter } from 'next/router'
import { constructS3Url } from 'utils'

const EDPVideo = lazy(() => import('components/elements/Video/Video'))

// Todo: these quote, classification and hygraphQuote interfaces
// may benefit of being moved to a shared location
interface QuoteProps {
  quoteTitle: string
  quoteDescription: {
    html: string
  }
  quoteSubHeadline: string
  quoteCopy: {
    html: string
  }
}

interface ClassificationProps {
  classficiationTitle: string
  classficiationDescription: string
  classficiationRating: number
  classficiationRatingIcon?: ImageProps
  starImageUrlS3?: string
}

interface HygraphQuoteProps {
  quoteTitle?: string
  quoteDescription?: {
    html: string
  }
  quoteSubHeadline?: string
  quoteCopy?: {
    html: string
  }
  classficiationTitle?: string
  classficiationDescription?: string
  classficiationRating?: number
  classficiationRatingIcon?: ImageProps
  starImageUrlS3?: string
}

interface EDPFullScreenVideoProps {
  backgroundVideo: {
    backgroundVideoXl: { url: string }
    backgroundVideoMd: { url: string }
    backgroundVideoSm: { url: string }
    videoLoop: boolean
    posterImageXlUrlS3?: string
    posterImageMdUrlS3?: string
    posterImageSmUrlS3?: string
    // For storybook compatibility
    posterImageXl?: { url: string }
    posterImageMd?: { url: string }
    posterImageSm?: { url: string }
  }
  ctaPrimary?: ButtonProps
  quote: Array<HygraphQuoteProps>
  businessUnit?: string
}

const EDPFullScreenVideo = ({ backgroundVideo, ctaPrimary, quote, businessUnit }: EDPFullScreenVideoProps) => {
  const {
    backgroundVideoXl,
    backgroundVideoMd,
    backgroundVideoSm,
    posterImageXl,
    posterImageMd,
    posterImageSm,
    videoLoop,
    posterImageXlUrlS3,
    posterImageMdUrlS3,
    posterImageSmUrlS3
  } = backgroundVideo
  const { isBreakpoint: isAboveBreakpointMd } = getCustomBreakpoint(768, '>')
  const { isBreakpoint: isAboveBreakpointXL } = getCustomBreakpoint(1439, '>')

  const edpVideo = isAboveBreakpointXL ? backgroundVideoXl : isAboveBreakpointMd ? backgroundVideoMd : backgroundVideoSm

  // Determine the appropriate poster image based on the current breakpoint
  // Use the S3 URLs if available, otherwise fall back to the provided poster images
  const propPosterImage = isAboveBreakpointXL ? posterImageXl : isAboveBreakpointMd ? posterImageMd : posterImageSm
  const s3PosterImage = isAboveBreakpointXL ? posterImageXlUrlS3 : isAboveBreakpointMd ? posterImageMdUrlS3 : posterImageSmUrlS3
  const posterImage = s3PosterImage ? { url: constructS3Url(s3PosterImage) } : { url: propPosterImage?.url || '' }

  const { query } = useRouter()

  // Ensure slug is iterable before spreading
  const slugSegments = Array.isArray(query.slug) ? query.slug : []
  const [source] = [...slugSegments].reverse()

  const gaBtnClick = (buttonProps: ButtonProps) => {
    gaBuyTicketsClick({
      businessUnit,
      buyType: buttonProps.buyType,
      clickSource: source ?? '',
      clickTracking: `${window.location.href} edp_full_screen_video:buy_tickets`,
      eventDate: '',
      eventVenueName: 'sphere_vegas',
      eventCategory: 'other',
      eventType: 'other events',
      eventDataStatus: '',
      linkLocation: EDPFullScreenVideo.displayName,
      linkPosition: '',
      ticketmasterEventId: '',
      cmsIdentifier: buttonProps.cmsIdentifier,
      buttonPlacementDescription: buttonProps.buttonPlacementDescription
    })
  }

  const [wrappedQuote] = quote
  const quoteObject = wrappedQuote?.quoteTitle && (wrappedQuote as QuoteProps)
  const classification = wrappedQuote?.classficiationTitle && (wrappedQuote as ClassificationProps)

  return (
    <>
      <div className={styles['edp-full-screen-video']}>
        {ctaPrimary && isAboveBreakpointMd && (
          <div className={cx(styles['cta-container'], styles['is-visible'])}>
            <Button dataID='edp-buy-btn' onClick={() => gaBtnClick(ctaPrimary)} {...ctaPrimary} size={'lg'} />
          </div>
        )}
        {quoteObject && <Quote classification={quoteObject} />}
        {classification && <Classification classification={classification} />}
        <Suspense fallback={<>Loading...</>}>
          <EDPVideo videoLoop={videoLoop} posterImage={posterImage} backgroundVideo={edpVideo} />
        </Suspense>
      </div>
    </>
  )
}

EDPFullScreenVideo.displayName = 'edp_full_screen_video'

export default EDPFullScreenVideo
